import { useState, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Dialog,
  Form,
  Input,
  Select,
  Switch,
  Popconfirm,
  Textarea,
  Space,
  MessagePlugin,
  Tabs,
} from 'tdesign-react';
import { AddIcon, EditIcon, DeleteIcon } from 'tdesign-icons-react';
import { productIntroApi } from '../../api';
import type { ProductIntroDTO, ProductAdvantage } from '../../types/banner';
import CoverImageUploader from '../../components/CoverImageUploader';

export default function ProductIntroManage() {
  const [data, setData] = useState<ProductIntroDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingProductIntro, setEditingProductIntro] = useState<ProductIntroDTO | null>(null);
  const [imageUrl, setImageUrl] = useState('');
  const [productImage, setProductImageUrl] = useState<string[]>([]);
  const [advantages, setAdvantages] = useState<ProductAdvantage[]>([]);
  const [activeAdvantageTab, setActiveAdvantageTab] = useState(0);
  const [searchForm] = Form.useForm();

  const formRef = useRef<any>(null);

  const columns = [
    // { colKey: 'id', title: 'ID', width: 80 },
    {
      colKey: 'coverImage',
      title: '封面图',
      width: 150,
      cell: ({ row }: any) => (
        <img
          src={row.coverImage}
          alt="封面图"
          className="w-20 h-12 object-cover rounded"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      ),
    },
    { colKey: 'title', title: '标题', ellipsis: true },
    {
      colKey: 'status',
      title: '状态',
      width: 100,
      cell: ({ row }: any) => (
        <Switch value={row.status === 1} onChange={(val) => handleStatusChange(row, val)} />
      ),
    },
    { colKey: 'viewCount', title: '浏览量', width: 80 },
    { colKey: 'sortOrder', title: '排序', width: 80 },
    { colKey: 'createTime', title: '创建时间', width: 180 },
    { colKey: 'updateTime', title: '更新时间', width: 180 },
    {
      colKey: 'action',
      title: '操作',
      width: 200,
      cell: ({ row }: any) => (
        <Space>
          <Button theme="success" variant="text" icon={<EditIcon />} onClick={() => handleEdit(row.id)}>编辑</Button>
          <Popconfirm content="确定删除该产品介绍吗？" onConfirm={() => handleDelete(row.id)}>
            <Button theme="danger" variant="text" icon={<DeleteIcon />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];
  
  interface FetchParams {
    pageNum?: number;
    pageSize?: number;
  }
  //加载数据
  const fetchData = async (params : FetchParams = {}) => {
    setLoading(true);
    try {
        const rawSearchParams  = searchForm.getFieldsValue?.(true) || {};
      // 清洗参数：移除 undefined, null 和空字符串
      const searchParams = Object.keys(rawSearchParams).reduce((acc, key) => {
        const value = rawSearchParams[key];
        if (value !== undefined && value !== null && value !== '') {
          acc[key] = value;
        }
        return acc;
      }, {} as any);
      const res = await productIntroApi.getPageList({ 
        pageNum: pagination.current,
        pageSize: pagination.pageSize,
        ...searchParams,
        ...params,
      });
      if (res.code === 200) {
        setData(res.data.list);
        setPagination({
          ...pagination,
          total: res.data.total,
          ...(params.pageNum && { current: params.pageNum }),
          ...(params.pageSize && { pageSize: params.pageSize }),
        });
      }
    } catch {
      MessagePlugin.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);
  //重置表单状态
  const resetFormState = () => {
    setImageUrl('');
    setProductImageUrl([]);
    setAdvantages([]);
    setEditingProductIntro(null);
    if (formRef.current) {
      formRef.current.reset();
    }
  };

  // 添加产品优势
  const handleAddAdvantage = () => {
    const newAdvantages = [...advantages, {
      title: '',
      content: '',
      image: '',
      subtitle: '',
      sortOrder: advantages.length,
    }];
    setAdvantages(newAdvantages);
    setActiveAdvantageTab(newAdvantages.length - 1);
  };

  // 删除产品优势
  const handleRemoveAdvantage = (index: number) => {
    const newAdvantages = advantages.filter((_, i) => i !== index);
    setAdvantages(newAdvantages);
    if (activeAdvantageTab >= newAdvantages.length) {
      setActiveAdvantageTab(Math.max(0, newAdvantages.length - 1));
    }
  };

  // 修改产品优势字段
  const handleAdvantageChange = (index: number, field: keyof ProductAdvantage, value: string) => {
    const newAdvantages = [...advantages];
    newAdvantages[index] = { ...newAdvantages[index], [field]: value };
    setAdvantages(newAdvantages);
  };
    //搜索
  const handleSearch = () => {
    setPagination({ ...pagination, current: 1 });
    fetchData({ pageNum: 1 });
  };
  //重置
  const handleReset = () => {
    searchForm.reset();
    setPagination({ ...pagination, current: 1 });
    fetchData({ pageNum: 1 });
  };
  //新增
  const handleAdd = () => {
    setEditingProductIntro(null);
    setImageUrl('');
    setProductImageUrl([]);
    setAdvantages([]);
    setActiveAdvantageTab(0)
    if (formRef.current) {
      formRef.current.reset();
    }
    setDialogVisible(true);
  };
  //编辑
  const handleEdit = async (id: number) => {
    try {
      const res = await productIntroApi.getById(id);
      if (res.code === 200) {
        const data = res.data;
        data.status = data.status === 1 ? true : false;
        setEditingProductIntro(data);
        setImageUrl(data.coverImage || '');
        setProductImageUrl(Array.isArray(data.images) ? data.images : []);
        setAdvantages(Array.isArray(data.advantages) ? data.advantages : []);
        setActiveAdvantageTab(0)
      } else {
        MessagePlugin.error(res.message || '获取详情失败');
      }
    } catch {
      MessagePlugin.error('获取详情失败');
    }
  };

  useEffect(() => {
    if (editingProductIntro) {
      setDialogVisible(true);
    }
  }, [editingProductIntro]);
  //提交
  const handleSubmit = async () => {
    try {
      if (!imageUrl) {
        MessagePlugin.error('请上传图片');
        return;
      }

      const result = await formRef.current?.validate();
      if (result !== true) {
        return;
      }

      const values = formRef.current?.getFieldsValue(true) || {};
      const submitData: ProductIntroDTO = {
        ...values,
        coverImage: imageUrl,
        images: productImage,
        advantages: advantages,
        status: values.status ? 1 : 0,
        sortOrder: values.sortOrder ? Number(values.sortOrder) : 0,
      };

      const res = editingProductIntro
        ? await productIntroApi.update(editingProductIntro.id, submitData)
        : await productIntroApi.create(submitData);

      if (res.code === 200) {
        MessagePlugin.success(editingProductIntro ? '更新成功' : '创建成功');
        setDialogVisible(false);
        resetFormState();
        fetchData();
      } else {
        MessagePlugin.error(res.message || '操作失败');
      }
    } catch (err) {
      console.error('提交失败：', err);
      MessagePlugin.error('表单验证失败或服务器异常');
    }
  };
  //删除
  const handleDelete = async (id: number) => {
    try {
      const res = await productIntroApi.delete(id);
      if (res.code === 200) {
        MessagePlugin.success('删除成功');
        fetchData();
      } else {
        MessagePlugin.error(res.message || '删除失败');
      }
    } catch {
      MessagePlugin.error('删除失败');
    }
  };
  //状态切换
  const handleStatusChange = async (productIntro: ProductIntroDTO, enabled: boolean) => {
    try {
      const res = await productIntroApi.update(productIntro.id, { status: enabled ? 1 : 0 });
      if (res.code === 200) {
        MessagePlugin.success(enabled ? '已启用' : '已禁用');
        fetchData();
      } else {
        MessagePlugin.error(res.message || '操作失败');
      }
    } catch {
      MessagePlugin.error('操作失败');
    }
  };

  return (
    <div className="p-4">
        <div className="flex justify-between items-center mb-6">
            <h1 className="text-xl font-bold">产品介绍管理</h1>
            <Button icon={<AddIcon />} theme="primary" onClick={handleAdd}>新增产品介绍</Button>
        </div>
    
        <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
            <Form form={searchForm} layout="inline">
                <Form.FormItem name="status" label="状态">
                    <Select placeholder="请选择状态" clearable style={{ width: 120 }}>
                        <Select.Option value={1}>启用</Select.Option>
                        <Select.Option value={0}>禁用</Select.Option>
                    </Select>
                </Form.FormItem>
                <Form.FormItem name="type" label="类型">
                    <Select placeholder="请选择类型" clearable style={{ width: 120 }}>
                        <Select.Option value="brand">首页轮播</Select.Option>
                        <Select.Option value="mall">商城轮播</Select.Option>
                    </Select>
                </Form.FormItem>
                <Form.FormItem>
                    <Button theme="primary" onClick={handleSearch}>查询</Button>
                    <Button style={{ marginLeft: 8 }} onClick={handleReset}>重置</Button>
                </Form.FormItem>
            </Form>
        </div>

      <Table
        columns={columns}
        data={data}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          onChange: (pageInfo: { current: number; pageSize: number }) => {
            const { current, pageSize } = pageInfo;
            setPagination({ current, pageSize, total: pagination.total });
            fetchData({ pageNum: current, pageSize });
          },
        }}
        rowKey="id"
      />

      <Dialog
        header={editingProductIntro ? '编辑产品介绍' : '新增产品介绍'}
        visible={dialogVisible}
        onClose={() => {
          setDialogVisible(false);
          resetFormState();
        }}
        onConfirm={handleSubmit}
        confirmBtn="保存"
        cancelBtn="取消"
        width={800}
        key={editingProductIntro ? `edit-${editingProductIntro.id}` : 'add-new'}
      >
        <Form
          ref={formRef}
          initialData={editingProductIntro || {}}
          labelWidth={100}
        >
          <Form.FormItem label="标题" name="title" rules={[{ required: true, message: '请输入产品介绍标题' }]}>
            <Input placeholder="请输入产品介绍标题" />
          </Form.FormItem>
          <Form.FormItem label="描述" name="description">
            <Textarea placeholder="请输入产品介绍描述" />
          </Form.FormItem>

          <Form.FormItem label="封面图" name="coverImage" rules={[{ required: true, message: '请上传封面图' }]}>
            <CoverImageUploader
              value={imageUrl}
              onChange={(value) => setImageUrl(Array.isArray(value) ? value[0] : value)}
              simple
            />
          </Form.FormItem>
          <Form.FormItem label="产品图" name="images">
            <CoverImageUploader
              value={productImage}
              onChange={(value) => setProductImageUrl(Array.isArray(value) ? value : [])}
            />
          </Form.FormItem>

          <Form.FormItem label="状态" name="status" initialValue={true}>
            <Switch />
          </Form.FormItem>

          <Form.FormItem label="排序" name="sortOrder">
            <Input type="number" placeholder="请输入排序值" />
          </Form.FormItem>  

          <div className="border-t my-4"></div>
          <div className="mb-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-medium"></h3>
              <Button icon={<AddIcon />} variant="text" onClick={handleAddAdvantage}>添加优势</Button>
            </div>
            <Form.FormItem label="产品优势" name="advantages">
              <div className='w-full'>
                {advantages.length > 0 ? (
                  <Tabs 
                    value={activeAdvantageTab} 
                    onChange={(value) => setActiveAdvantageTab(value as number)}
                    theme="card"
                    dragSort
                  >
                    {advantages.map((adv, index) => (
                      <Tabs.TabPanel 
                        key={adv.id || index} 
                        value={index} 
                        label={`优势 ${index + 1}`}
                        removable={true}
                        onRemove={() => handleRemoveAdvantage(index)}
                      >
                        <div className="bg-gray-50 p-4 border">
                          <div className="flex justify-between items-center mb-3">
                            <span className="text-sm text-gray-500 w-24 font-medium">优势标题</span>
                            <Input
                              placeholder="请输入优势标题"
                              value={adv.title}
                              onChange={(val) => handleAdvantageChange(index, 'title', val)}
                              className="flex-1"
                            />
                          </div>
                          <div className="flex justify-between items-start mb-3">
                            <span className="text-sm text-gray-500 w-24 font-medium self-start pt-1">描述</span>
                            <Textarea
                              placeholder="请输入描述"
                              rows={3}
                              value={adv.content}
                              onChange={(val) => handleAdvantageChange(index, 'content', val)}
                              className="flex-1"
                            />
                          </div>
                          <div className="flex items-center mb-3">
                            <span className="text-sm text-gray-500 w-24 font-medium">图片</span>
                            <CoverImageUploader
                              value={adv.image || ''}
                              onChange={(val) => handleAdvantageChange(index, 'image', Array.isArray(val) ? val[0] || '' : val)}
                              simple
                            />
                          </div>
                        </div>
                      </Tabs.TabPanel>
                    ))}
                  </Tabs>
                ) : (
                  <div className="text-center py-8 bg-gray-50 rounded-lg border">
                    <p className="text-gray-400">暂无产品优势，点击上方按钮添加</p>
                  </div>
                )}
              </div>
            </Form.FormItem> 
          </div>
        </Form>
      </Dialog>
    </div>
  );
}
